import { toast } from 'sonner';
import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { Calendar, FileText, Plus, UserRound } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { StatsCard } from '../components/StatsCard';
import { SearchInput } from '../components/SearchInput';
import { RecordCount } from '../components/RecordCount';
import { LoadingState } from '../components/LoadingState';
import { PatientAvatar } from '../components/PatientAvatar';
import { PersonalHomeTiles } from '../components/PersonalHomeTiles';
import { useIsPersonalEdition } from '@/lib/auth/useEdition';
import { P, RequirePermission } from '@/features/permissions';
import { SimpleTable } from '@/components/table/simple-table';
import { getSelectionColumn } from '@/components/table/selection-column';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';
import { TableFilterSection } from '@/components/table/table-filter-section';
import { bulkDeleteAction, TableBulkActions } from '@/components/table/bulk-action-bar';
import { type PatientOut, useBulkDeletePatients, usePatients } from '../api/patients-api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

function getPatientName(metadata: import('../api/patients-api').PatientMetadata | null): {
  first: string;
  last: string;
} {
  if (!metadata) return { last: '', first: '' };
  return {
    last: metadata.last_name ?? '',
    first: metadata.first_name ?? '',
  };
}

const columns: ColumnDef<PatientOut>[] = [
  getSelectionColumn<PatientOut>(),
  {
    id: 'mrn',
    size: 160,
    header: 'MRN',
    accessorKey: 'medical_record_number',
    cell: ({ row }) => <span className="font-mono text-sm">{row.original.medical_record_number}</span>,
  },
  {
    id: 'name',
    header: 'Patient Name',
    accessorFn: (row) => {
      const { last, first } = getPatientName(row.metadata_);
      return `${last} ${first}`.trim();
    },
    cell: ({ row }) => {
      const { last, first } = getPatientName(row.original.metadata_);
      const displayName = first || last ? `${last}, ${first}` : row.original.medical_record_number;
      return (
        <div className="flex items-center gap-3">
          <PatientAvatar lastName={last} firstName={first} />
          <p className="font-medium">{displayName}</p>
        </div>
      );
    },
  },
  {
    id: 'source',
    header: 'Source',
    accessorKey: 'patient_source',
    cell: ({ row }) =>
      row.original.patient_source ? (
        <Badge variant="outline" className="text-xs">
          {row.original.patient_source}
        </Badge>
      ) : null,
  },
  {
    id: 'created',
    header: 'Created',
    accessorKey: 'created_at',
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">{format(new Date(row.original.created_at), 'MMM d, yyyy')}</span>
    ),
  },
];

export function PatientsPage() {
  const navigate = useNavigate();
  const setPageHeader = usePageHeaderSetter();
  const isPersonal = useIsPersonalEdition();
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const { data, isLoading } = usePatients({
    limit: 100,
    search: !isPersonal && search ? search : undefined,
  });
  const bulkDelete = useBulkDeletePatients();

  useEffect(() => {
    setPageHeader({
      title: isPersonal ? 'Home' : 'Patients',
      actions: (
        <RequirePermission permission={P.CREATE_PATIENT}>
          <Button size="sm" onClick={() => navigate({ to: '/patients/new' })}>
            <Plus className="mr-2 h-4 w-4" />
            {isPersonal ? 'Add User' : 'Add Patient'}
          </Button>
        </RequirePermission>
      ),
    });
    return () => setPageHeader(null);
  }, [setPageHeader, navigate, isPersonal]);

  const allPatients = useMemo(() => data?.data ?? [], [data]);

  const patients = useMemo(() => {
    if (sourceFilter === 'all') return allPatients;
    return allPatients.filter((p) => p.patient_source === sourceFilter);
  }, [allPatients, sourceFilter]);

  const sources = useMemo(() => {
    const s = new Set<string>();
    for (const p of allPatients) {
      if (p.patient_source) s.add(p.patient_source);
    }
    return [...s].sort();
  }, [allPatients]);

  if (isLoading) return <LoadingState />;

  return (
    <div className={`flex flex-col gap-6 p-4 md:p-6 ${isPersonal ? 'mx-auto w-full max-w-3xl' : ''}`}>
      {/* Personal-edition action tiles — Quick Start + camera scan */}
      {isPersonal && <PersonalHomeTiles />}

      {/* Stats — hidden in personal edition (per Avi: tiles deferred until content confirmed) */}
      {!isPersonal && (
        <div className="grid gap-4 grid-cols-2 md:grid-cols-3">
          <StatsCard icon={UserRound} label="Total Patients" value={data?.meta.total_items ?? 0} />
          <StatsCard
            icon={Calendar}
            label="New This Month"
            value={
              allPatients.filter((p) => {
                if (!p.created_at) return false;
                const created = new Date(p.created_at);
                const now = new Date();
                return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
              }).length
            }
          />
          <StatsCard icon={FileText} label="Sources" value={sources.length} />
        </div>
      )}

      {/* Search + Filters — hidden in personal edition for a cleaner shell */}
      {!isPersonal && (
        <div className="flex flex-col gap-3">
          <SearchInput value={search} placeholder="Search by name or MRN..." onChange={setSearch} />
          <TableFilterSection
            activeFilterCount={sourceFilter !== 'all' ? 1 : 0}
            onClearAll={() => setSourceFilter('all')}>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Source</Label>
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="w-full sm:w-[160px]">
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {sources.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </TableFilterSection>
        </div>
      )}

      {/* Table */}
      <SimpleTable<PatientOut>
        data={patients}
        columns={columns}
        getRowId={(row) => row.id}
        onRowClick={(row) => navigate({ to: '/patients/$patientId', params: { patientId: row.id } })}
        actions={(table) => (
          <TableBulkActions
            table={table}
            actions={(rows) => [
              bulkDeleteAction({
                rows,
                entityName: 'patient',
                getId: (r) => r.id,
                onDelete: async (ids) => {
                  await bulkDelete.mutateAsync(ids);
                  table.resetRowSelection();
                  toast.success(`Deleted ${ids.length} patient${ids.length === 1 ? '' : 's'}`);
                },
              }),
            ]}
          />
        )}
      />

      <RecordCount entity="patients" filtered={patients.length} total={allPatients.length} />
    </div>
  );
}
